import React, { createContext, useContext, useState, useCallback } from 'react';
import api from './api';
import { trackEvent } from './analytics';

const AuthContext = createContext(null);

function persistProfessional(nextProfessional) {
  if (nextProfessional) {
    localStorage.setItem('bf_professional', JSON.stringify(nextProfessional));
    return;
  }

  localStorage.removeItem('bf_professional');
}

function setAuthSession(token, nextProfessional) {
  localStorage.setItem('bf_token', token);
  persistProfessional(nextProfessional);
}

export function AuthProvider({ children }) {
  const [professional, setProfessional] = useState(() => {
    try {
      const stored = localStorage.getItem('bf_professional');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAuthSession(data.token, data.professional);
    setProfessional(data.professional);
    trackEvent('login_success', {
      login_host: typeof window !== 'undefined' ? window.location.hostname : '',
      professional_id: data.professional?.id || '',
      booking_slug: data.professional?.booking_slug || '',
    });
    return data.professional;
  }, []);

  const register = useCallback(async ({ name, email, phone, password }) => {
    const { data } = await api.post('/auth/register', { name, email, phone, password });
    setAuthSession(data.token, data.professional);
    setProfessional(data.professional);
    trackEvent('signup_success', {
      signup_host: typeof window !== 'undefined' ? window.location.hostname : '',
      professional_id: data.professional?.id || '',
      booking_slug: data.professional?.booking_slug || '',
    });
    return data.professional;
  }, []);

  const updateProfessional = useCallback((nextProfessional) => {
    setProfessional(nextProfessional);
    persistProfessional(nextProfessional);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('bf_token');
    persistProfessional(null);
    setProfessional(null);
  }, []);

  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('bf_token');
  const isLoggedIn = !!professional || hasToken;

  return (
    <AuthContext.Provider value={{ professional, login, register, logout, updateProfessional, isLoggedIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
